import api from "./api";
import { normalizeFloor } from "./floorService";
import { resolveRoleName } from "../utils/auth";

// Building service (singular endpoints)
// Endpoints:
// - GET /building
// - GET /building/:id
// - POST /building
// - PATCH /building/:id
// - DELETE /building/:id

const normalizeBuilding = (dto) => ({
  building_id: dto?.building_id ?? dto?.id,
  building_code: dto?.building_code ?? dto?.code ?? dto?.buildingCode ?? "",
  name: dto?.name ?? "",
  company_id: dto?.company_id ?? null,
  building_type_id: dto?.building_type_id ?? null,
  // The expanded type, when the endpoint sends it. Kept so the floor pages can
  // read its code straight from here instead of fetching the whole catalogue.
  building_type: dto?.building_type ?? null,
  regionId: dto?.regionId ?? dto?.region_id ?? dto?.region?.id ?? null,
  region: dto?.region ?? null,
  countryId: dto?.countryId ?? dto?.country_id ?? null,
  cityId: dto?.cityId ?? dto?.city_id ?? null,
  countryIso3: dto?.country?.isoCode3 ?? dto?.isoCode3 ?? null,
  countryIso2: dto?.country?.isoCode2 ?? dto?.isoCode2 ?? null,
  country:
    dto?.country?.countryName ?? dto?.country?.name ?? dto?.country ?? "",
  city: dto?.city?.cityName ?? dto?.city?.name ?? dto?.city ?? "",
  company: dto?.company ?? null,
  rows: dto?.rows ?? 0,
  columns: dto?.columns ?? 0,
  floors: dto?.floors ?? 0,
  // Optional layout markers if provided by backend
  stairs_cell: dto?.stairs_cell ?? null,
  elevator_cell: dto?.elevator_cell ?? null,
  // Timestamps (support snake_case and camelCase)
  created_at: dto?.created_at ?? dto?.createdAt ?? null,
  updated_at: dto?.updated_at ?? dto?.updatedAt ?? null,
});

const normalizeBuildingWithRelations = (dto) => {
  const base = normalizeBuilding(dto);
  const rawFloors =
    Array.isArray(dto?.floors) ? dto.floors
    : Array.isArray(dto?.floor) ? dto.floor
    : [];
  return {
    ...base,
    company: dto?.company ?? null,
    // floors in with-relations are an array of floor DTOs
    floors: rawFloors.map(normalizeFloor),
    // include cells array if provided by backend
    cells: (() => {
      const rawCells =
        Array.isArray(dto?.cells) ? dto.cells
        : Array.isArray(dto?.building_cell) ? dto.building_cell
        : [];
      return rawCells.map((c) => ({
        id: c?.id,
        building_id: c?.building_id ?? base.building_id ?? null,
        row: Number(c?.row),
        column: Number(c?.column),
        type: String(c?.type || "").toUpperCase(),
      }));
    })(),
  };
};

const extractBuildingArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeRoleKey = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

const getCurrentAuthUser = () => {
  try {
    const raw = sessionStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  try {
    const raw = localStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  return null;
};

async function getCurrentUserRoleKey() {
  const user = getCurrentAuthUser();
  if (!user) return "";
  const roleName = await resolveRoleName(user);
  return normalizeRoleKey(roleName);
}

export async function getBuildings() {
  const data = await api.get("/building");
  return extractBuildingArray(data).map(normalizeBuilding);
}

export async function getBuildingsPaginated({
  page = 1,
  limit = 10,
  search = "",
} = {}) {
  const query = new URLSearchParams();
  if (page != null) query.append("page", String(page));
  if (limit != null) query.append("limit", String(limit));
  if (String(search || "").trim()) {
    query.append("search", String(search).trim());
  }
  const qs = query.toString();
  const roleKey = await getCurrentUserRoleKey();
  const endpoint =
    roleKey === "SUPERADMIN" ? "/building" : (
      "/hierarchical-view/relation/building/bulk"
    );
  const res = await api.get(`${endpoint}${qs ? `?${qs}` : ""}`);

  const data = extractBuildingArray(res);
  const meta =
    res?.meta && typeof res.meta === "object" ?
      res.meta
    : {
        total: data.length,
        page: Number(page) || 1,
        limit: Number(limit) || 10,
        totalPages: 1,
      };

  return {
    data: data.map(normalizeBuilding),
    meta: {
      total: Number(meta.total) || 0,
      page: Number(meta.page) || 1,
      limit: Number(meta.limit) || Number(limit) || 10,
      totalPages: Number(meta.totalPages) || 1,
    },
  };
}

export async function getBuildingById(id) {
  const res = await api.get(`/building/${id}`);
  return res ? normalizeBuilding(res) : null;
}

/**
 * Requests for the same building that overlap in time, sharing one round trip.
 *
 * The floor pages mount more than one reader of this payload at once — the page
 * itself for the grid size, and the layout designer for the building type and
 * the sibling storeys. Same reasoning as the floor equivalent in floorService:
 * deliberately NOT a TTL cache, because every later read is a refresh after a
 * write and has to see the server's new state. Only concurrent callers share,
 * and the entry is dropped as soon as it settles.
 */
const _buildingRelationsInFlight = new Map();

export async function getBuildingWithRelations(id) {
  const key = String(id);
  let request = _buildingRelationsInFlight.get(key);

  if (!request) {
    request = api
      .get(`/building/${id}/with-relations`)
      .finally(() => _buildingRelationsInFlight.delete(key));
    _buildingRelationsInFlight.set(key, request);
  }

  // Normalized per caller: the raw payload is shared, the object graph is not.
  const res = await request;
  return res ? normalizeBuildingWithRelations(res) : null;
}

export async function createBuilding(payload) {
  const body = {
    name: String(payload.name || "").trim(),
    company_id: payload.company_id != null ? Number(payload.company_id) : null,
    regionId:
      payload.regionId !== undefined ? payload.regionId
      : payload.region_id !== undefined ? payload.region_id
      : undefined,
    countryId:
      payload.countryId != null && payload.countryId !== "" ?
        Number(payload.countryId)
      : undefined,
    cityId:
      payload.cityId != null && payload.cityId !== "" ?
        Number(payload.cityId)
      : undefined,
    rows: Number(payload.rows) || 0,
    columns: Number(payload.columns) || 0,
    building_type_id:
      payload.building_type_id != null && payload.building_type_id !== "" ?
        Number(payload.building_type_id)
      : null,
  };
  if (body.regionId !== undefined) {
    if (body.regionId === null || body.regionId === "") body.regionId = null;
    else body.regionId = Number(body.regionId);
  }
  if (body.countryId === undefined) delete body.countryId;
  if (body.cityId === undefined) delete body.cityId;
  if (
    payload.building_id !== undefined &&
    payload.building_id !== null &&
    payload.building_id !== ""
  ) {
    body.building_id = Number(payload.building_id);
  }
  // Map to camelCase only per backend spec
  if (
    payload.building_code !== undefined ||
    payload.buildingCode !== undefined
  ) {
    const code = String(payload.building_code ?? payload.buildingCode).trim();
    body.buildingCode = code;
  }
  // Pass layout array to backend when provided (for stairs/elevator cells)
  if (Array.isArray(payload.layout)) {
    body.layout = payload.layout.map((it) => ({
      row: Number(it.row),
      column: Number(it.column),
      type: String(it.type || "").toUpperCase(),
    }));
  }
  const res = await api.post("/building", body);
  return res ? normalizeBuilding(res) : normalizeBuilding(body);
}

export async function patchBuilding(id, payload) {
  const body = {};
  if (payload.name !== undefined) body.name = String(payload.name).trim();
  if (payload.company_id !== undefined)
    body.company_id = Number(payload.company_id);
  if (payload.building_type_id !== undefined)
    body.building_type_id =
      payload.building_type_id != null && payload.building_type_id !== "" ?
        Number(payload.building_type_id)
      : null;
  if (payload.regionId !== undefined || payload.region_id !== undefined) {
    const value =
      payload.regionId !== undefined ? payload.regionId : payload.region_id;
    body.regionId = value === null || value === "" ? null : Number(value);
  }
  if (payload.countryId !== undefined)
    body.countryId = Number(payload.countryId);
  if (payload.cityId !== undefined) body.cityId = Number(payload.cityId);
  if (payload.rows !== undefined) body.rows = Number(payload.rows) || 0;
  if (payload.columns !== undefined)
    body.columns = Number(payload.columns) || 0;
  if (payload.floors !== undefined) body.floors = Number(payload.floors) || 0; // keep if backend supports
  if (
    payload.building_code !== undefined ||
    payload.buildingCode !== undefined
  ) {
    const code = String(payload.building_code ?? payload.buildingCode).trim();
    body.buildingCode = code; // camelCase only per backend spec
  }
  // Pass layout array to backend when provided
  if (Array.isArray(payload.layout)) {
    body.layout = payload.layout.map((it) => ({
      row: Number(it.row),
      column: Number(it.column),
      type: String(it.type || "").toUpperCase(),
    }));
  }
  // Do NOT send legacy stairs_cell/elevator_cell; backend expects layout array only
  if (
    payload.building_id !== undefined &&
    Number(payload.building_id) !== Number(id)
  )
    body.id = Number(payload.building_id);
  const res = await api.patch(`/building/${id}`, body);
  return res ?
      normalizeBuilding(res)
    : normalizeBuilding({ id: body.id ?? id, ...body });
}

export async function deleteBuilding(id) {
  return api.delete(`/building/${id}`);
}

export async function deleteBuildingsBulk(ids = []) {
  await Promise.all(ids.map((i) => deleteBuilding(i)));
}

// POST /hierarchical-view/relation/building/bulk
// Body: { userIds: number[] }
// Aggregates associatedCompanyBuildings + createdCompanyBuildings for each user, de-duplicated by building_id
export async function getBuildingsByUsersBulk(userIds = []) {
  // Guard: SUPER_ADMIN must not call bulk; keep behavior unchanged by returning full buildings
  try {
    const raw = sessionStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    const currentUser = auth?.user;
    if (currentUser) {
      const rn = await resolveRoleName(currentUser);
      const norm = String(rn || "")
        .toUpperCase()
        .replace(/[^A-Z]/g, "");
      if (norm === "SUPERADMIN") {
        return getBuildings();
      }
    }
  } catch {
    // ignore and proceed
  }
  const payload = {
    userIds: (userIds || [])
      .map((x) => Number(x))
      .filter((n) => Number.isInteger(n)),
  };
  if (payload.userIds.length === 0) return [];
  const key = payload.userIds
    .slice()
    .sort((a, b) => a - b)
    .join(",");
  if (!getBuildingsByUsersBulk._inflight)
    getBuildingsByUsersBulk._inflight = new Map();
  if (getBuildingsByUsersBulk._inflight.has(key)) {
    return await getBuildingsByUsersBulk._inflight.get(key);
  }
  const p = (async () => {
    const res = await api.post(
      "/hierarchical-view/relation/building/bulk",
      payload,
    );
    const results = Array.isArray(res?.results) ? res.results : [];
    const set = new Map();
    for (const item of results) {
      if (!item || item.success !== true) continue;
      const data = item.data || {};
      const assocArr =
        Array.isArray(data.associatedCompanyBuildings) ?
          data.associatedCompanyBuildings
        : [];
      for (const b of assocArr) {
        const norm = normalizeBuilding(b);
        if (norm.building_id != null) set.set(Number(norm.building_id), norm);
      }
      const createdArr =
        Array.isArray(data.createdCompanyBuildings) ?
          data.createdCompanyBuildings
        : [];
      for (const b of createdArr) {
        const norm = normalizeBuilding(b);
        if (norm.building_id != null) set.set(Number(norm.building_id), norm);
      }
    }
    return Array.from(set.values());
  })();
  getBuildingsByUsersBulk._inflight.set(key, p);
  try {
    return await p;
  } finally {
    getBuildingsByUsersBulk._inflight.delete(key);
  }
}

export async function getBuildingsByCompanyId(companyId) {
  try {
    const data = await api.get(`/building/companyId/${companyId}`);
    return Array.isArray(data) ? data.map(normalizeBuilding) : [];
  } catch (e) {
    console.error("Failed to fetch buildings by company ID:", e);
    return [];
  }
}
